import Phaser from 'phaser';
import { GameScene } from './GameScene';
import { MainMenuScene } from './MainMenuScene';
import { ensurePlayerName, getPlayerName, isPlayerNameCustomized, setPlayerName, submitScore } from '../services/leaderboard';
import { isNameAllowed } from '../util/profanity';
import { ensureTierTextures, queueShapeImageLoads, bakeTierImagesIntoTextures, textureKey, TIERS } from '../game/shapes';
import { fmt } from '../util/format';
import { createButton } from '../util/button';
import { activePalette, css } from '../util/palette';

const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';

interface GameOverData {
  score: number;
  highScore: number;
  reason?: 'dead-line' | 'fall-out';
  escapedTier?: number;
  mergeCounts?: Record<number, number>;
}

export class GameOverScene extends Phaser.Scene {
  static readonly KEY = 'GameOverScene';

  constructor() {
    super({ key: GameOverScene.KEY });
  }

  preload(): void {
    queueShapeImageLoads(this);
  }

  create(data: GameOverData): void {
    bakeTierImagesIntoTextures(this);
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;
    const pal = activePalette();

    // Full-screen dim
    this.add.rectangle(0, 0, width, height, 0x000000, 0.75).setOrigin(0).setDepth(100);

    // Modal card
    const isNewBest = data.score >= data.highScore && data.score > 0;
    const showShape = data.reason === 'fall-out' && typeof data.escapedTier === 'number';
    const baseH = showShape ? 560 : 500;
    // Wide screens (tablet / desktop) get a substantially larger card — there's
    // horizontal room to spare. Height-guarded so short desktop windows still
    // fit the whole card, floored at 1 so phones are untouched.
    const s = width >= 700
      ? Math.max(1, Math.min(1.35, (height - 60) / baseH, (width - 48) / 360))
      : 1;
    const cardW = Math.min(width - 40, Math.round(360 * s));
    const cardH = Math.round(baseH * s);
    const cardX = cx - cardW / 2;
    const cardY = cy - cardH / 2;

    const card = this.add.graphics().setDepth(101);
    // Solid fill, like the how-to-play modal. (fillGradientStyle + rounded
    // rect doesn't fill opaquely — the pile showed through the card.)
    card.fillStyle(pal.surfaceDeep, 1);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 20);
    card.lineStyle(2.5, pal.danger, 0.7);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 20);

    let contentY = cardY + Math.round(42 * s);

    // Title
    this.add
      .text(cx, contentY, 'GAME OVER', {
        fontFamily: FONT,
        fontSize: `${Math.round(36 * s)}px`,
        fontStyle: 'bold',
        color: css(pal.danger),
        resolution: DPR,
      })
      .setOrigin(0.5)
      .setDepth(102);
    contentY += Math.round(42 * s);

    // Reason
    const reasonText =
      data.reason === 'fall-out' ? 'A shape escaped the container' : 'Pile crossed the dead line';
    this.add
      .text(cx, contentY, reasonText, {
        fontFamily: FONT,
        fontSize: `${Math.round(14 * s)}px`,
        color: css(pal.textMuted),
        resolution: DPR,
      })
      .setOrigin(0.5)
      .setDepth(102);
    contentY += Math.round(36 * s);

    // Escaped shape (fall-out only) — scooted up to leave room for merge counts
    if (showShape) {
      this.renderEscapedShape(cx, contentY + Math.round(20 * s), data.escapedTier!, s);
      contentY += Math.round(58 * s);
    }

    // Divider
    const divG = this.add.graphics().setDepth(102);
    divG.lineStyle(1, pal.surfaceBorder, 1);
    divG.beginPath();
    divG.moveTo(cardX + 24, contentY);
    divG.lineTo(cardX + cardW - 24, contentY);
    divG.strokePath();
    contentY += Math.round(44 * s);

    // Score
    this.add
      .text(cx, contentY, fmt(data.score), {
        fontFamily: FONT,
        fontSize: `${Math.round(52 * s)}px`,
        fontStyle: 'bold',
        color: css(pal.text),
        resolution: DPR,
      })
      .setOrigin(0.5)
      .setDepth(102);
    contentY += Math.round(50 * s);

    // New best badge
    if (isNewBest) {
      this.add
        .text(cx, contentY, '★ NEW BEST ★', {
          fontFamily: FONT,
          fontSize: `${Math.round(15 * s)}px`,
          fontStyle: 'bold',
          color: css(pal.coin),
          resolution: DPR,
        })
        .setOrigin(0.5)
        .setDepth(102);
    } else {
      this.add
        .text(cx, contentY, `Best: ${fmt(data.highScore)}`, {
          fontFamily: FONT,
          fontSize: `${Math.round(15 * s)}px`,
          color: css(pal.textMuted),
          resolution: DPR,
        })
        .setOrigin(0.5)
        .setDepth(102);
    }
    contentY += Math.round(28 * s);

    // Leaderboard submit status
    this.submitAndShowStatus(data.score, cx, contentY);
    contentY += Math.round(26 * s);

    // Merge counts
    this.renderMergeCounts(cx, contentY, cardW, data.mergeCounts ?? {}, s);

    // Buttons row: MENU (left) + PLAY AGAIN (right)
    const btnH = Math.round(52 * s);
    const btnY = cardY + cardH - btnH / 2 - Math.round(20 * s);

    const menuBtn = createButton(this, {
      x: cx - Math.round(92 * s), y: btnY, width: Math.round(104 * s), height: btnH,
      label: 'MENU', fontSize: Math.round(16 * s), variant: 'secondary', color: pal.primary, textColor: css(pal.primary),
      depth: 102,
      onClick: () => {
        this.scene.stop();
        this.scene.stop(GameScene.KEY);
        this.scene.start(MainMenuScene.KEY);
      },
    });

    const playBtn = createButton(this, {
      x: cx + Math.round(62 * s), y: btnY, width: Math.round(172 * s), height: btnH,
      label: 'PLAY AGAIN', fontSize: Math.round(18 * s), depth: 102,
      onClick: () => {
        this.scene.stop();
        this.scene.stop(GameScene.KEY);
        this.scene.start(GameScene.KEY);
      },
    });

    // Fade in
    const all = [card, menuBtn, playBtn];
    all.forEach((o) => o.setAlpha(0));
    this.tweens.add({ targets: all, alpha: 1, duration: 200 });
  }

  private renderMergeCounts(cx: number, y: number, cardW: number, mergeCounts: Record<number, number>, s = 1): void {
    ensureTierTextures(this);
    const pal = activePalette();
    this.add.text(cx, y + Math.round(10 * s), 'MERGES', {
      fontFamily: FONT, fontSize: `${Math.round(13 * s)}px`, fontStyle: 'bold', color: css(pal.textLabel), resolution: DPR,
    }).setOrigin(0.5).setDepth(102);

    // Triangle (tier 0) is spawned, never created by merging — omit from the row.
    // King (tier 9) is the result of merging two Octagons — include it.
    const displayTiers = TIERS.slice(1);
    const MIN_SIZE = Math.round(20 * s);
    const MAX_SIZE = Math.round(44 * s);
    const iconY = y + Math.round(40 * s);
    const countBaseline = iconY + MAX_SIZE / 2 + Math.round(10 * s);

    // Space shapes by a uniform edge-to-edge gap rather than uniform centers:
    // equal cells make the small shapes look farther apart and let the large
    // ones overflow the card.
    const sizes = displayTiers.map((_, i) => {
      const t = displayTiers.length > 1 ? i / (displayTiers.length - 1) : 0;
      return MIN_SIZE + t * (MAX_SIZE - MIN_SIZE);
    });
    const innerW = cardW - 24;
    const totalIconW = sizes.reduce((a, b) => a + b, 0);
    const gap = Math.max(0, (innerW - totalIconW) / (displayTiers.length - 1));
    let x = cx - innerW / 2 + sizes[0] / 2;

    for (let i = 0; i < displayTiers.length; i++) {
      const count = mergeCounts[displayTiers[i].index] ?? 0;
      const alpha = count > 0 ? 1 : 0.18;
      const tier = displayTiers[i];
      const scale = sizes[i] / (tier.radius * 2 + 6);
      this.add.image(x, iconY, textureKey(tier.index))
        .setOrigin(0.5).setScale(scale).setAlpha(alpha).setDepth(102);
      if (count > 0) {
        this.add.text(x, countBaseline, `${count}`, {
          fontFamily: FONT, fontSize: `${Math.round(12 * s)}px`, fontStyle: 'bold', color: css(pal.text), resolution: DPR,
        }).setOrigin(0.5).setDepth(102);
      }
      if (i < displayTiers.length - 1) x += sizes[i] / 2 + gap + sizes[i + 1] / 2;
    }
  }

  private renderEscapedShape(cx: number, y: number, tierIndex: number, s = 1): void {
    if (tierIndex < 0 || tierIndex >= TIERS.length) return;
    ensureTierTextures(this);
    const tier = TIERS[tierIndex];
    const targetSize = Math.round(56 * s);
    const scale = Math.min(1, targetSize / (tier.radius * 2));
    const sprite = this.add
      .image(cx, y, textureKey(tierIndex))
      .setOrigin(0.5)
      .setScale(scale)
      .setDepth(102);
    this.tweens.add({ targets: sprite, y: y - 5, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  /**
   * Scores always submit immediately under the player's name — a generated
   * default like "Shmerger4821" on first play, so there's never an input gate.
   * The status line is tappable to rename at any time, and is deliberately
   * loud (bigger, gold, pulsing) until the player has picked their own name.
   */
  private submitAndShowStatus(score: number, cx: number, y: number): void {
    if (score <= 0) return;
    const pal = activePalette();

    const label = (n: string) =>
      isPlayerNameCustomized() ? `Submitted as ${n} ✏️` : `Submitted as ${n} — tap to rename ✏️`;

    const showNameInput = () => {
      const el = document.createElement('input');
      el.type = 'text';
      el.maxLength = 16;
      el.value = getPlayerName() || '';
      el.placeholder = 'your name';
      Object.assign(el.style, {
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '18px', padding: '10px 14px',
        border: `2px solid ${css(pal.button)}`, borderRadius: '10px',
        background: css(pal.surfaceDeep), color: css(pal.text),
        outline: 'none', zIndex: '9999', width: '210px',
        textAlign: 'center', fontFamily: FONT,
      });
      document.body.appendChild(el);
      el.focus();
      el.select();
      this.events.once('shutdown', () => {
        if (document.body.contains(el)) document.body.removeChild(el);
      });
      const finish = () => {
        const val = el.value.trim().slice(0, 16);
        if (!val || val === getPlayerName()) {
          if (document.body.contains(el)) document.body.removeChild(el);
          return;
        }
        // The leaderboard is public, so reject objectionable nicknames (the
        // server enforces this too). Keep the prior name and nudge a retry.
        if (!isNameAllowed(val)) {
          if (document.body.contains(el)) document.body.removeChild(el);
          status.setText('that name isn’t allowed — tap to rename ✏️');
          return;
        }
        if (document.body.contains(el)) document.body.removeChild(el);
        setPlayerName(val);
        calmStatus();
        doSubmit(val);
      };
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') finish();
        if (e.key === 'Escape' && document.body.contains(el)) document.body.removeChild(el);
      });
      el.addEventListener('blur', finish);
    };

    const doSubmit = (n: string) => {
      status.setText(label(n));
      void submitScore(score, n)
        .then(() => status.setText(label(n)))
        .catch(() => status.setText(`Failed — tap to retry as ${n} ✏️`));
    };

    const name = ensurePlayerName();
    const firstTime = !isPlayerNameCustomized();
    const status = this.add
      .text(cx, y, label(name), {
        fontFamily: FONT, fontSize: firstTime ? '14px' : '12px', fontStyle: firstTime ? 'bold' : 'normal',
        color: css(firstTime ? pal.coin : pal.primary), resolution: DPR,
      })
      .setOrigin(0.5).setDepth(102).setInteractive({ useHandCursor: true });

    // Gentle breathing pulse draws the eye to the rename affordance while the
    // player is still on the generated default.
    let pulse: Phaser.Tweens.Tween | undefined;
    if (firstTime) {
      pulse = this.tweens.add({
        targets: status, scale: 1.07, duration: 650, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    }
    const calmStatus = () => {
      pulse?.remove();
      pulse = undefined;
      status.setScale(1).setFontSize(12).setFontStyle('normal').setColor(css(pal.primary));
    };

    doSubmit(name);

    status.on('pointerup', showNameInput);
  }
}
